import { UserProfile } from '@/features/users/types';
import { BoxV2 as Box, useHasPermission, Tooltip } from 'portal-commons';
import React, { useEffect, useMemo, useState } from 'react';
import {
  CampaignDetail,
  CampaignOperationDcaItem,
  CampaignOperationItem,
} from '../../types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEllipsisVertical,
  faInfoCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { faSquareCheck, faSquareX } from '@fortawesome/pro-light-svg-icons';
import { capitalize } from 'lodash-es';
import { Menu } from '@/components';
import CarrierSuspendModal from './CarrierSuspendModal';
import CarrierLiftSuspensionModal from './CarrierLiftSuspensionModal';
import { getSuspensionCategoryOptions } from '@/apis/services';
import { SelectItem } from '@/types';
import DcaSelectionModal from './DcaSelectionModal';

interface Props {
  profile: UserProfile;
  campaign: CampaignDetail;
  carriers: CampaignOperationItem[];
  onUpdatedCampaignOperations: () => void;
  existingChain: boolean;
  provisional: boolean;
  onDcaMenuClick: () => void;
}

interface MenuItem {
  label: string;
  value: string;
  disabled?: boolean;
}

const headRows = [
  'MNO',
  'Qualifying',
  'MNO Review',
  'Campaign Status',
  'Message Class',
  'Brand Tier',
  'Sharing Status', // only for primary DCA
  'Elected DCA', // only for primary DCA
  'Action',
];

type Action = 'selectDca' | 'updateDca' | 'suspend' | 'liftSuspension';

const ValidityIcon: React.FC<{
  valid?: boolean | null;
  fallback?: React.ReactNode;
}> = ({ valid, fallback = '' }) => {
  if (valid === undefined || valid === null) {
    return <>{fallback}</>;
  }

  return (
    <FontAwesomeIcon
      fontSize="16px"
      icon={valid ? faSquareCheck : faSquareX}
      color={valid ? '#000' : '#999FA0'}
    />
  );
};

const CarrierStatusTable: React.FC<Props> = ({
  profile,
  campaign,
  carriers,
  onUpdatedCampaignOperations,
  existingChain,
  provisional,
  onDcaMenuClick,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [activeMno, setActiveMno] = useState<string | null>(null);
  const [activeCarrier, setActiveCarrier] =
    useState<CampaignOperationItem | null>(null);
  const [modalAction, setModalAction] = useState<Action | null>(null);
  const [suspensionCategories, setSuspensionCategories] = useState<
    SelectItem[]
  >([]);
  const isSoleProprietor = campaign.usecase === 'SOLE_PROPRIETOR';
  const isRedacted = isSoleProprietor && !profile.dpaSigned;

  const { isPrimaryDca } = campaign || {};
  const isExpired = campaign.status === 'EXPIRED';
  const hasExclusions = !!campaign.excludedMnoList?.length;

  const removeHeaders = ['Sharing Status', 'Elected DCA'];
  const tableHeadRow = isPrimaryDca
    ? headRows
    : headRows.filter((head) => !removeHeaders.includes(head));

  const excludedMnos = useMemo(
    () =>
      campaign.excludedMnoList?.map((excludedMno) => excludedMno.name) ?? [],
    [campaign.excludedMnoList]
  );

  const fetchSuspensionCategories = async () => {
    const options = await getSuspensionCategoryOptions();
    setSuspensionCategories(options);
  };

  useEffect(() => {
    fetchSuspensionCategories();
  }, []);

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    carrier: CampaignOperationItem
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setActiveCarrier(carrier);
    setActiveMno(carrier.rulesEngineResultsJson.mno);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setActiveMno(null);
  };

  const handleModalClose = (reload: boolean) => {
    setActiveCarrier(null);
    setActiveMno(null);
    setModalAction(null);
    if (reload) {
      onUpdatedCampaignOperations();
    }
  };

  const findLastDca = (
    carrier: CampaignOperationItem
  ): CampaignOperationDcaItem | null => {
    let lastDca: CampaignOperationDcaItem | null = null;
    if (carrier.dcas && carrier.dcas.length > 0) {
      let lastTimestamp = carrier.dcas[0].createDate - 1;
      for (let i = 0; i < carrier.dcas.length; i++) {
        const dca = carrier.dcas[i];
        if (dca.createDate > lastTimestamp) {
          lastDca = dca;
          lastTimestamp = dca.createDate;
        }
      }
    }
    return lastDca;
  };

  const getLastDcaName = (carrier: CampaignOperationItem) => {
    const lastDca = findLastDca(carrier);
    return lastDca ? lastDca.name : 'N/A';
  };

  const getLastDcaStatus = (carrier: CampaignOperationItem) => {
    const lastDca = findLastDca(carrier);
    const lastStatus = lastDca ? lastDca.status.toLowerCase() : '';
    return lastDca && (lastStatus === 'pending' || lastStatus === 'accepted')
      ? capitalize(lastStatus)
      : 'N/A';
  };

  const isResponsibleForMno = (carrier: CampaignOperationItem) => {
    const lastDca = findLastDca(carrier);
    return (
      lastDca &&
      (lastDca.id === profile.uid || lastDca.status.toLowerCase() === 'pending')
    );
  };

  const permissions = useHasPermission([
    'campaignDetail.liftSuspendMnoStatus',
    'campaignDetail.suspendMnoStatus',
    'campaignDetail.selectShareCarrierStatus',
    'campaignDetail.liftSuspensionCarrierStatus',
    'campaignDetail.suspendCarrierStatus',
    'campaignDetail.editDcaCarrierStatus',
  ]);
  const getMenuItems = (carrier: CampaignOperationItem): MenuItem[] => {
    const menu: MenuItem[] = [];

    // DCA 2
    if (!isPrimaryDca) {
      if (carrier.allowedNewStatus?.includes('SUSPENDED')) {
        menu.push({
          label: 'Suspend',
          value: 'suspend',
          disabled:
            !existingChain ||
            !permissions['campaignDetail.suspendMnoStatus'] ||
            isExpired,
        });
      }
      if (carrier.allowedNewStatus?.includes('REGISTERED')) {
        menu.push({
          label: 'Lift Suspension',
          value: 'liftSuspension',
          disabled:
            !existingChain ||
            !permissions['campaignDetail.liftSuspendMnoStatus'] ||
            isExpired,
        });
      }
      return menu;
    }

    // DCA 1
    const lastDcaStatus = getLastDcaStatus(carrier);
    if (lastDcaStatus === 'N/A') {
      menu.push({
        label: 'Select DCA',
        value: 'selectDca',
        disabled:
          !permissions['campaignDetail.selectShareCarrierStatus'] || isExpired,
      });
      return menu;
    }

    // lastDcaStatus = 'Pending' | 'Accepted'
    const isResponsible = isResponsibleForMno(carrier);
    if (carrier.allowedNewStatus?.includes('SUSPENDED')) {
      menu.push({
        label: 'Suspend',
        value: 'suspend',
        disabled:
          !existingChain ||
          !isResponsible ||
          isExpired ||
          !permissions['campaignDetail.suspendCarrierStatus'],
      });
    }
    if (carrier.allowedNewStatus?.includes('REGISTERED')) {
      menu.push({
        label: 'Lift Suspension',
        value: 'liftSuspension',
        disabled:
          !existingChain ||
          !isResponsible ||
          isExpired ||
          !permissions['campaignDetail.liftSuspensionCarrierStatus'],
      });
    }

    menu.push({
      label: 'Update DCA',
      value: 'updateDca',
      disabled:
        lastDcaStatus === 'Pending' ||
        !permissions['campaignDetail.editDcaCarrierStatus'] ||
        isExpired,
    });

    return menu;
  };

  const onActionClick = (mno: string, action: Action) => {
    if (['selectDca', 'updateDca'].includes(action)) {
      onDcaMenuClick();
    }
    setModalAction(action);
  };

  return (
    <Box sx={{ mt: 'l' }}>
      <Box
        as="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          backgroundColor: 'white',
          height: 'auto',
          borderBottom: `${hasExclusions ? '1px solid #E6E7E7' : 'none'}`,
        }}
        data-testid="carrierStatusTable"
      >
        <Box as="thead" sx={{ backgroundColor: 't.blue800' }}>
          <Box as="tr">
            {tableHeadRow.map((head, index) => (
              <TableHeader
                isLast={index === tableHeadRow.length - 1}
                key={head}
              >
                {head}
              </TableHeader>
            ))}
          </Box>
        </Box>
        <Box
          as="tbody"
          sx={{
            tr: {
              borderBottom: '1px solid',
              borderColor: '#E6E7E7',
              '&:last-child': { borderBottom: 'none' },
            },
            td: {
              padding: '11px 10px 11px 16px',
              fontSize: 'H300',
              color: '#101820',
            },
          }}
        >
          {carriers.map((carrier) => {
            const mnoData = carrier.rulesEngineResultsJson;
            const isExcluded = excludedMnos.includes(mnoData.mno);
            const menuItems = isExcluded ? [] : getMenuItems(carrier);
            return (
              <Box as="tr" key={mnoData.mno}>
                <td data-testid="carrierStatusTableMnoName">
                  {mnoData.mno || '--'}
                </td>
                <td data-testid="carrierStatusTableQualifying">
                  <ValidityIcon valid={mnoData.qualify} fallback="--" />
                </td>
                <td data-testid="carrierStatusTableMnoReview">
                  <ValidityIcon valid={mnoData.mnoReview} fallback="--" />
                </td>
                <td data-testid="carrierStatusTableCampaignStatus">
                  {carrier.status.toLowerCase() === 'approved'
                    ? 'Registered'
                    : capitalize(carrier.status)}

                  {carrier.status.toLowerCase() === 'suspended' &&
                    carrier.statusIssuerRole && (
                      <Tooltip
                        title={`Suspended by ${carrier.statusIssuerRole}`}
                        placement="bottom-end"
                        arrow
                      >
                        <span
                          style={{
                            marginLeft: '12px',
                            color: '#B2531F',
                          }}
                          data-testid="carrierItemSuspendedTooltip"
                        >
                          <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                      </Tooltip>
                    )}
                </td>
                <td data-testid="carrierStatusTableMessageClass">
                  {mnoData.mno === 'T-Mobile' ? '--' : mnoData.msgClass || '--'}
                </td>
                <td data-testid="carrierStatusTableBrandTier">
                  {mnoData.mno === 'T-Mobile'
                    ? capitalize(mnoData.brandTier)
                    : '--'}
                </td>
                {isPrimaryDca && (
                  <>
                    <td data-testid="carrierStatusTableSharingStatus">
                      {isExcluded ? 'Excluded' : getLastDcaStatus(carrier)}
                    </td>
                    <td data-testid="carrierStatusTableElectedDca">
                      {isExcluded ? '--' : getLastDcaName(carrier)}
                    </td>
                  </>
                )}
                <td data-testid="carrierStatusTableAction">
                  {menuItems.length > 0 && (
                    <>
                      <Box
                        role="button"
                        onClick={(e) => handleMenuClick(e, carrier)}
                        sx={{ cursor: 'pointer' }}
                        data-testid="carrierStatusTableActionButton"
                      >
                        <FontAwesomeIcon icon={faEllipsisVertical} />
                      </Box>
                      <Menu
                        open={activeMno === mnoData.mno}
                        anchorEl={menuAnchorEl}
                        onClose={handleMenuClose}
                        onItemClick={(value) =>
                          onActionClick?.(mnoData.mno, value as Action)
                        }
                        sx={{ minWidth: 120 }}
                        data-testid="carrierStatusTableActionMenu"
                      >
                        {menuItems.map((item) => (
                          <Menu.MenuItem
                            key={item.value}
                            value={item.value}
                            disabled={item.disabled ?? false}
                            data-testid={`menuItem-${item.value}`}
                          >
                            {item.label}
                          </Menu.MenuItem>
                        ))}
                      </Menu>
                    </>
                  )}
                </td>
              </Box>
            );
          })}
        </Box>
      </Box>
      {activeCarrier && modalAction && (
        <>
          <CarrierSuspendModal
            open={modalAction === 'suspend'}
            campaign={campaign}
            categories={suspensionCategories}
            carrier={activeCarrier}
            isRedacted={isRedacted}
            onClose={handleModalClose}
          />
          <CarrierLiftSuspensionModal
            open={modalAction === 'liftSuspension'}
            campaign={campaign}
            carrier={activeCarrier}
            isRedacted={isRedacted}
            onClose={handleModalClose}
          />
          <DcaSelectionModal
            open={modalAction === 'selectDca' || modalAction === 'updateDca'}
            type={modalAction === 'selectDca' ? 'select' : 'update'}
            campaign={campaign}
            carrier={activeCarrier}
            provisional={provisional}
            onClose={handleModalClose}
          />
        </>
      )}
    </Box>
  );
};

const TableHeader: React.FC<{ isLast: boolean }> = ({ children, isLast }) => {
  return (
    <Box
      as="th"
      sx={{
        padding: '12px 8px 8px 16px',
        textAlign: 'left',
        verticalAlign: 'text-top',
        color: 'white',
        fontWeight: 700,
        whiteSpace: 'nowrap',
        pl: isLast ? '8px' : undefined,
        height: 38,
      }}
    >
      {children}
    </Box>
  );
};

export default CarrierStatusTable;
